import { AnalyticsEventMap, PageEventProps } from '@easeltv/analytics';
import { useLocalStorage } from '@easeltv/hooks';
import {
    createContext,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { Helmet } from 'react-helmet-async';

import { useConfig } from '../../hooks/useConfig/useConfig';
import { useSession } from '../../hooks/useSession/useSession';
import {
    Consent,
    CONSENT_VERSION,
    initAnalytics,
} from './AnalyticsProvider.helpers';

/* ---------------------------------- Types --------------------------------- */

export type TriggerType = keyof AnalyticsEventMap;

/**
 * AnalyticsContextI
 *
 * Shape of the context object returned for use within the AnalyticsProvider
 */
export interface AnalyticsContextI {
    /** An object containing current page data, useful for tracking */
    pageObject?: PageEventProps;

    /** Method for updating the details in the pageObject */
    updatePageObject: (page: PageEventProps) => void;

    /** Holds the current type of tracking to trigger */
    triggerType?: TriggerType;

    /** Method for changing the type of tracking trigger */
    setTriggerType: (type: TriggerType) => void;
}

/* --------------------------------- Context -------------------------------- */

export const AnalyticsContext = createContext<AnalyticsContextI | undefined>(
    undefined,
);

/* ---------------------------------- Hook ---------------------------------- */

/**
 * useAnalytics
 *
 * Hook provides access to the AnalyticsContext from within the AnalyticsProvider
 *
 * @returns AnalyticsContext
 */
export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);

    if (!context) {
        throw Error(
            'Cannot use AnalyticsContext outside the AnalyticsProvider',
        );
    }

    return context;
};

/* -------------------------------- Provider -------------------------------- */

/**
 * AnalyticsProvider
 *
 * @param { children } - The ReactNodes to be wrapped with this provider giving access to the AnalyticsContext
 * @returns
 */
const AnalyticsProvider = ({ children }: PropsWithChildren) => {
    const { analytics, name: appName } = useConfig();
    const session = useSession();

    const [consent] = useLocalStorage<Consent>('easel.consent', {
        version: CONSENT_VERSION,
        decision: 'empty',
    });

    const [pageObject, setPageObject] = useState<PageEventProps | undefined>(
        undefined,
    );

    const [triggerType, setTriggerType] = useState<TriggerType | undefined>(
        undefined,
    );

    const updatePageObject = useCallback((currentPage: PageEventProps) => {
        setPageObject(currentPage);
        window.analytics?.trigger({
            type: 'app.viewed',
            currentPage,
        });
    }, []);

    const value = useMemo(
        () => ({
            pageObject,
            updatePageObject,
            triggerType,
            setTriggerType,
        }),
        [pageObject, updatePageObject, triggerType, setTriggerType],
    );

    useEffect(() => {
        initAnalytics(analytics, session?.id, session?.userId, consent.decision, appName);
    }, [analytics, session?.id, session?.userId, consent.decision, appName]);

    useEffect(() => {
        if (consent.decision === 'all') {
            window.analytics?.enable();
        } else {
            window.analytics?.disable();
        }
    }, [consent.decision]);

    return (
        <AnalyticsContext.Provider value={value}>
            <Helmet>
                <link
                    rel="dns-prefetch"
                    href="https://www.googletagmanager.com"
                />
            </Helmet>
            {children}
        </AnalyticsContext.Provider>
    );
};

export default AnalyticsProvider;
